import { useState, useEffect } from 'react';
import Header from '../components/navBar';
import Footer from '../components/footer';
import ProductCard from '../components/discount/productCard';
import { Link } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Recommended from '../components/product_profile/recommend';

const EarringsPage = () => {
  const [products, setProducts] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch products from Firestore on component mount
  useEffect(() => {
    async function fetchProducts() {
      const backendURL = process.env.REACT_APP_BACKEND_URL;

      try {
        // Adjust the URL if your Express server is hosted elsewhere.
        const response = await fetch(`${backendURL}products/products?category=earrings`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to fetch products.');
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, []);

  // Sorting logic based on sortBy state.
  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
    //   case 'rating':
    //     return (b.rating || 0) - (a.rating || 0);
      default:
        return 0;
    }
  });

  // Since the query already limits products to "necklace", we use the sorted products directly.
  const filteredProducts = sortedProducts;

  if (loading) {
    return (
      <section className="bg-white antialiased dark:bg-gray-900">
        <Header />
        <div className="py-16 sm:py-24 mx-auto max-w-screen-xl px-4 2xl:px-0">
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <Skeleton height={180} />
                <Skeleton className="mt-2" height={20} width="80%" />
                <Skeleton className="mt-1" height={15} width="60%" />
                <Skeleton className="mt-2" height={25} width="50%" />
              </div>
            ))}
          </div>
        </div>
        <Footer />
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white antialiased dark:bg-gray-900">
        <Header />
        <div className="py-16 sm:py-24 mx-auto max-w-screen-xl px-4 2xl:px-0">
          <p className="text-center text-gray-600 dark:text-gray-400">{error}</p>
        </div>
        <Footer />
      </section>
    );
  }

  return (
    <section className="bg-white antialiased dark:bg-gray-900">
      <Header />
      <div className="py-16 sm:py-24 mx-auto max-w-screen-xl px-4 2xl:px-0">
        {/* Breadcrumbs and header */}
        <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-end justify-between space-y-4 sm:space-y-0 md:mb-8">
          <div>
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
                <li className="inline-flex items-center">
                  <Link
                    to="/homepage"
                    className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-yellow-900 dark:text-gray-400 dark:hover:text-white"
                  >
                    <svg
                      className="me-2.5 h-3 w-3"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z" />
                    </svg>
                    Home
                  </Link>
                </li>
                <li aria-current="page">
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 text-gray-400 rtl:rotate-180"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m9 5 7 7-7 7"
                      />
                    </svg>
                    <span className="ms-1 text-sm font-medium text-gray-500 dark:text-gray-400 md:ms-2">
                      Earrings
                    </span>
                  </div>
                </li>
              </ol>
            </nav>
            <h2 className="mt-3 text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              Earrings
            </h2>
          </div>

          {/* Only sort controls remain */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
            >
              <option value="">Sort By</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              {/* <option value="rating">Rating</option> */}
            </select>
          </div>
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
      <Recommended/>
      <Footer />
    </section>
  );
};

export default EarringsPage;
